/**
 * Les critères de recherche — **un seul module, deux formes**.
 *
 * C'est le jumeau TypeScript de `ListingSearchCriteria` et de `SearchCriteriaCodec` côté serveur.
 * Il sérialise dans les deux sens : query string (ce que l'URL porte) et objet (ce que l'API
 * consomme, et ce que `seeking.saved_search.criteria_json` conserve).
 *
 * **Pourquoi un seul module.** Le §1.9 de la conception L3 en fait un point de conception, pas de
 * rangement : si l'URL et le document enregistré étaient produits à deux endroits, ils
 * divergeraient, et un lien partagé ne rendrait pas ce que son auteur avait gardé. Ici, une
 * recherche enregistrée est *littéralement* une URL.
 *
 * Les noms de paramètres font partie du contrat au même titre qu'un champ de DTO : les renommer
 * casserait les liens déjà partagés et les recherches déjà enregistrées.
 */

export type NatureUniteRecherche = 'EntireProperty' | 'PrivateRoom' | 'SharedRoom'
export type TypeBailRecherche = 'SingleJoint' | 'Individual'
export type ModeTrajet = 'Walk' | 'Drive'
export type Tri = 'Relevance' | 'PriceAscending' | 'PriceDescending' | 'Newest'
export type Frequence = 'None' | 'Instant' | 'Daily'

export interface Criteres {
  text?: string
  /**
   * Ville — code du référentiel, paramètre `city` (D44, conception L13 §2.5).
   *
   * Absent, le serveur applique la ville par défaut et le dit (`appliedCityCode`) : une URL
   * partagée avant L13 garde son sens. Ce n'est PAS un filtre — c'est le périmètre dans lequel les
   * filtres s'appliquent — et « tout effacer » le conserve.
   */
  cityCode?: string
  /** Quartier en saisie libre — la forme normalisée rendue par `/districts` (§1.1). */
  district?: string
  /** Quartier référencé. Nul tant que les polygones ne sont pas chargés (§1.1). */
  neighborhoodId?: string
  south?: number
  west?: number
  north?: number
  east?: number
  minRent?: number
  maxRent?: number
  availableFrom?: string
  unitKind?: NatureUniteRecherche
  minBedrooms?: number
  furnished?: boolean
  leaseType?: TypeBailRecherche
  amenityCodes?: string[]
  anchorCode?: string
  maxTravelMinutes?: number
  travelMode?: ModeTrajet
  verifiedOnly?: boolean
  withOriginalPhotosOnly?: boolean
}

/**
 * Correspondance nom de champ ⇄ nom de paramètre d'URL.
 *
 * Table explicite plutôt que conversion automatique : une règle de nommage implicite se casse au
 * premier champ qui n'y rentre pas, et le symptôme serait un filtre silencieusement ignoré.
 */
const PARAMS: Record<string, keyof Criteres> = {
  q: 'text',
  city: 'cityCode',
  district: 'district',
  neighborhoodId: 'neighborhoodId',
  south: 'south',
  west: 'west',
  north: 'north',
  east: 'east',
  minRent: 'minRent',
  maxRent: 'maxRent',
  availableFrom: 'availableFrom',
  unitKind: 'unitKind',
  minBedrooms: 'minBedrooms',
  furnished: 'furnished',
  leaseType: 'leaseType',
  anchor: 'anchorCode',
  maxMinutes: 'maxTravelMinutes',
  mode: 'travelMode',
  verifiedOnly: 'verifiedOnly',
  photosOnly: 'withOriginalPhotosOnly',
}

const NOMBRES = new Set<keyof Criteres>([
  'south',
  'west',
  'north',
  'east',
  'minRent',
  'maxRent',
  'minBedrooms',
  'maxTravelMinutes',
])

const BOOLEENS = new Set<keyof Criteres>(['furnished', 'verifiedOnly', 'withOriginalPhotosOnly'])

/** Lit les critères depuis une query string. Un paramètre illisible est ignoré, jamais fatal. */
export function lireCriteres(recherche: string): Criteres {
  const params = new URLSearchParams(recherche)
  const criteres: Record<string, unknown> = {}

  for (const [parametre, champ] of Object.entries(PARAMS)) {
    const brut = params.get(parametre)

    if (brut === null || brut === '') {
      continue
    }

    if (NOMBRES.has(champ)) {
      const valeur = Number(brut)
      if (Number.isFinite(valeur)) {
        criteres[champ] = valeur
      }
      continue
    }

    if (BOOLEENS.has(champ)) {
      if (brut === 'true' || brut === 'false') {
        criteres[champ] = brut === 'true'
      }
      continue
    }

    criteres[champ] = brut
  }

  // Les équipements sont répétés : ?amenity=WIFI&amenity=PARKING. Un paramètre à valeurs multiples
  // se lit mieux qu'une liste séparée par des virgules, que le premier code contenant une virgule
  // casserait.
  const equipements = params.getAll('amenity').filter((code) => code.length > 0)

  if (equipements.length > 0) {
    criteres.amenityCodes = equipements
  }

  return criteres as Criteres
}

/** Écrit les critères en query string. Les valeurs absentes ne laissent aucun paramètre vide. */
export function ecrireCriteres(criteres: Criteres, extra?: Record<string, string | undefined>): string {
  const params = new URLSearchParams()

  for (const [parametre, champ] of Object.entries(PARAMS)) {
    const valeur = criteres[champ]

    if (valeur === undefined || valeur === null || valeur === '') {
      continue
    }

    params.set(parametre, String(valeur))
  }

  for (const code of criteres.amenityCodes ?? []) {
    params.append('amenity', code)
  }

  for (const [cle, valeur] of Object.entries(extra ?? {})) {
    if (valeur !== undefined && valeur !== '') {
      params.set(cle, valeur)
    }
  }

  // Tri stable : deux jeux de critères identiques produisent la MÊME URL. Sans cela, deux liens
  // désignant la même recherche seraient différents, et le cache du navigateur les traiterait
  // comme deux pages.
  params.sort()

  return params.toString()
}

/**
 * La clé de `sessionStorage` qui retient la dernière recherche — volatile, et c'est voulu : un
 * onglet fermé n'a pas de recherche en cours. Écrite par la pastille de l'en-tête, relue par la
 * feuille de choix de la ville pour repartir avec les mêmes filtres.
 */
export const CLE_DERNIERE_RECHERCHE = 'coloc:derniere-recherche'

/** Les critères de la dernière recherche de la session, ou rien. */
export function derniereRecherche(): Criteres {
  try {
    return lireCriteres(globalThis.sessionStorage.getItem(CLE_DERNIERE_RECHERCHE) ?? '')
  } catch {
    return {}
  }
}

/** Le cadrage de la carte est-il complet ? Trois bornes sur quatre est une erreur, pas un cadrage. */
export function aUnCadrage(criteres: Criteres): boolean {
  return (
    criteres.south !== undefined &&
    criteres.west !== undefined &&
    criteres.north !== undefined &&
    criteres.east !== undefined
  )
}

/** Retire le cadrage : on quitte la carte, on garde les filtres. */
export function sansCadrage(criteres: Criteres): Criteres {
  const { south, west, north, east, ...reste } = criteres

  return reste
}

/**
 * Ce qui ne survit pas à un changement de ville : le cadrage, le quartier et le repère.
 *
 * Les trois désignent un lieu DANS la ville. Les garder en passant de Salé à Casablanca
 * demanderait au serveur un quartier de Salé dans le catalogue de Casablanca — zéro résultat,
 * sans qu'aucune puce ne dise pourquoi. Le budget, le type et les équipements, eux, voyagent.
 */
export function pourUneAutreVille(criteres: Criteres, cityCode: string): Criteres {
  const { district, neighborhoodId, anchorCode, maxTravelMinutes, ...reste } = sansCadrage(criteres)

  return { ...reste, cityCode }
}

/** Y a-t-il au moins un filtre actif ? Sert à afficher « réinitialiser ». */
export function aDesFiltres(criteres: Criteres): boolean {
  return Object.entries(criteres).some(([champ, valeur]) => {
    // La ville n'est pas un filtre : c'est le périmètre. Elle ne se « réinitialise » pas.
    if (['south', 'west', 'north', 'east', 'cityCode'].includes(champ)) {
      return false
    }

    return Array.isArray(valeur) ? valeur.length > 0 : valeur !== undefined && valeur !== ''
  })
}
